import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export interface AppUser {
  id: string;
  name: string;
  email: string;
  credits: number;
}

export type FeatureKey = "search";

interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  sendVerificationCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<{ verifyToken: string }>;
  register: (email: string, password: string, verifyToken: string) => Promise<void>;
  login: (email: string, password: string) => Promise<AppUser>;
  googleLogin: () => void;
  logout: () => Promise<void>;
  canUseFeature: (feature: FeatureKey) => boolean;
  consumeFeature: (feature: FeatureKey, updatedUser?: AppUser) => void;
  addCredits: (amount: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Validate session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<AppUser>("/api/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback((next: AppUser) => {
    setUser(next);
  }, []);

  const sendVerificationCode = useCallback(async (email: string) => {
    await api.post("/api/auth/send-code", { email });
  }, []);

  const verifyCode = useCallback(async (email: string, code: string) => {
    const data = await api.post<{ verify_token: string }>("/api/auth/verify-code", { email, code });
    return { verifyToken: data.verify_token };
  }, []);

  const register = useCallback(
    async (email: string, password: string, verifyToken: string) => {
      const user = await api.post<AppUser>("/api/auth/register", { email, password, verify_token: verifyToken });
      toast.success("注册成功，欢迎使用 DeepSearch");
      persist(user);
    },
    [persist],
  );

  const login = useCallback(async (email: string, password: string) => {
    const user = await api.post<AppUser>("/api/auth/login", { email, password });
    persist(user);
    return user;
  }, [persist]);

  const googleLogin = useCallback(() => {
    window.location.href = "/api/auth/google";
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout", {});
    } catch {
      // ignore logout errors
    }
    setUser(null);
    toast.success("已退出登录");
  }, []);

  const canUseFeature = useCallback(
    (_feature: FeatureKey) => {
      if (!user) return false;
      return user.credits > 0;
    },
    [user],
  );

  const consumeFeature = useCallback(
    (_feature: FeatureKey, updatedUser?: AppUser) => {
      if (!user) throw new Error("请先登录");
      if (updatedUser) {
        persist(updatedUser);
        return;
      }
      if (user.credits > 0) {
        persist({ ...user, credits: user.credits - 1 });
        return;
      }
      throw new Error("额度不足，请充值后继续");
    },
    [user, persist],
  );

  const addCredits = useCallback(
    async (amount: number) => {
      if (!user) return;
      const updated = await api.post<AppUser>("/api/auth/add-credits", { amount });
      persist(updated);
    },
    [user, persist],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      sendVerificationCode,
      verifyCode,
      register,
      login,
      googleLogin,
      logout,
      canUseFeature,
      consumeFeature,
      addCredits,
    }),
    [
      user,
      loading,
      sendVerificationCode,
      verifyCode,
      register,
      login,
      googleLogin,
      logout,
      canUseFeature,
      consumeFeature,
      addCredits,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
